import { MatchClockReading } from './build-video-marker';

/** Опорная точка, которую пора записать: хронометраж и идут ли игровые часы. */
export interface ClockAnchor extends MatchClockReading {
  running: boolean;
}

export interface ClockAnchorTrackerOptions {
  /** Сколько секунда хронометража может не меняться, пока часы считаются идущими. */
  stopAfterMs: number;
  /** Как часто ставить точку, пока часы идут. */
  intervalMs: number;
  /** Сколько показаний подряд должно продолжить скачок назад, чтобы он считался правкой статиста. */
  confirmReadings: number;
}

const DEFAULT_OPTIONS: ClockAnchorTrackerOptions = {
  stopAfterMs: 2_000,
  intervalMs: 30_000,
  confirmReadings: 3,
};

/** Насколько показание кандидата может обогнать ход часов и всё ещё продолжать его линию. */
const CANDIDATE_TOLERANCE_SECONDS = 1;

interface ClockState extends ClockAnchor {
  /** Когда секунда хронометража последний раз изменилась. */
  changedAt: number;
}

/** Скачок назад, который ещё не подтвердился: может быть правкой, а может — старой записью из очереди. */
interface BackwardCandidate {
  seconds: number;
  atMs: number;
  readings: number;
}

/**
 * Решает, когда ставить опорную точку «хронометраж матча ↔ позиция программы».
 *
 * Хронометраж (`matchChronology.currentTime`) приходит потоком `{ period, seconds }`. Точка
 * ставится при первом показании, при смене тайма, при старте и остановке игровых часов, при
 * ручной правке секунд статистом и раз в `intervalMs`, пока часы идут.
 *
 * Точки «часы идут» (`running: true`) ставятся в момент, когда секунда только что сменилась, —
 * им можно верить с точностью до тика, и только по ним пересчитывается время события
 * (`resolve-event-video-position.ts`). Остановку видно лишь спустя `stopAfterMs` тишины, поэтому
 * точка «часы стоят» запаздывает и служит признаком паузы, а не опорой.
 *
 * Остановка без новых показаний сама не случится: вызывающий раз в секунду зовёт `poll`.
 *
 * Скачок назад принимается не сразу. Приложение статиста после обрыва связи досылает очередь
 * посекундных записей разом и вперемешку со свежими (прогон IMGM-1484: 15, 5, 14, 5, 12, 8, 6, 13,
 * 16… за полторы секунды), и каждая старая секунда выглядела бы ручной правкой. Поэтому скачок
 * назад — только кандидат: правкой он становится, когда `confirmReadings` показаний подряд
 * продолжают его линию в темпе часов. Любое показание основной линии кандидата сбрасывает — у
 * очереди старые записи всегда перемешаны со свежими, у настоящей правки основной линии больше нет.
 */
export class ClockAnchorTracker {
  private readonly options: ClockAnchorTrackerOptions;
  private state: ClockState | null = null;
  private candidate: BackwardCandidate | null = null;
  private lastAnchorAt: number | null = null;

  constructor(options: Partial<ClockAnchorTrackerOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /** Новое показание хронометража; `atMs` — когда оверлей его получил. */
  observe(reading: MatchClockReading, atMs: number): ClockAnchor | null {
    const previous = this.state;

    // Начало наблюдения и новый тайм: идут ли часы, станет видно на следующей секунде.
    if (previous === null || previous.period !== reading.period) {
      this.candidate = null;
      this.state = { ...reading, running: false, changedAt: atMs };
      return this.emit(atMs);
    }

    if (reading.seconds < previous.seconds) return this.observeBackward(reading, atMs);

    // Показание основной линии: кандидат на правку оказался старой записью.
    this.candidate = null;
    if (previous.seconds === reading.seconds) return this.poll(atMs);

    const deltaMs = (reading.seconds - previous.seconds) * 1000;
    const elapsedMs = atMs - previous.changedAt;
    this.state = { ...reading, running: true, changedAt: atMs };

    const started = !previous.running;
    const correctedForward = deltaMs > elapsedMs + this.options.stopAfterMs;
    const intervalPassed = this.lastAnchorAt === null || atMs - this.lastAnchorAt >= this.options.intervalMs;

    return started || correctedForward || intervalPassed ? this.emit(atMs) : null;
  }

  /** Проверка остановки часов, если показания перестали приходить или не меняются. */
  poll(atMs: number): ClockAnchor | null {
    const state = this.state;
    if (state === null || !state.running) return null;
    if (atMs - state.changedAt <= this.options.stopAfterMs) return null;
    // Основная линия замолчала, потому что статист перевёл часы назад: ждём подтверждения правки.
    if (this.candidate !== null && atMs - this.candidate.atMs <= this.options.stopAfterMs) return null;

    state.running = false;
    return this.emit(atMs);
  }

  /** Забыть хронометраж — при смене матча или переподключении к программе. */
  reset() {
    this.state = null;
    this.candidate = null;
    this.lastAnchorAt = null;
  }

  private observeBackward(reading: MatchClockReading, atMs: number): ClockAnchor | null {
    const candidate = this.candidate;
    const continuesCandidate =
      candidate !== null &&
      reading.seconds >= candidate.seconds &&
      reading.seconds - candidate.seconds <= (atMs - candidate.atMs) / 1000 + CANDIDATE_TOLERANCE_SECONDS;

    if (!continuesCandidate) {
      this.candidate = { seconds: reading.seconds, atMs, readings: 1 };
    } else if (reading.seconds > candidate.seconds) {
      this.candidate = { seconds: reading.seconds, atMs, readings: candidate.readings + 1 };
    }

    if (this.candidate!.readings < this.options.confirmReadings) return null;

    this.candidate = null;
    this.state = { ...reading, running: true, changedAt: atMs };
    return this.emit(atMs);
  }

  private emit(atMs: number): ClockAnchor {
    const { period, seconds, running } = this.state!;
    this.lastAnchorAt = atMs;
    return { period, seconds, running };
  }
}
